import React from 'react';
import TiffRenderer from './TiffRenderer';
import ImageDownloadIcon from './ImageDownloadIcon';

// Document type configurations
const DOCUMENT_CONFIGS = {
  pdf: {
    keywords: ['pdf', 'application/pdf'],
    // icon: '📑',
    render: (docValue, docName) => (
      <iframe src={docValue} title={docName} className="document-iframe" />
    ),
  },
  image: {
    keywords: ['image/', 'image/jpeg', 'image/png', 'image/jpg', 'image/gif'],
    // icon: '🖼️',
    render: (docValue, docName) => (
      <div className="image-container w-100 d-inline-block position-relative">
        <img src={docValue} alt={docName} className="document-image" />
        <ImageDownloadIcon docValue={docValue} docName={docName} />
      </div>
    ),
  },
  tiff: {
    keywords: ['image/tiff', 'image/tif', 'tiff', 'tif'],
    // icon: '🖼️',
    render: (docValue, docName) => (
      <TiffRenderer docValue={docValue} docName={docName} />
    ),
  },
  word: {
    keywords: [
      'word',
      'msword',
      'officedocument.word',
      'application/vnd.openxmlformats-officedocument.wordprocessingml',
    ],
    // icon: '📄',
    render: (docValue, docName) => (
      <div className="document-preview">
        <div className="document-icon">📄</div>
        <a
          href={docValue}
          download={docName}
          className="document-download-link"
        >
          Download {docName}
        </a>
      </div>
    ),
  },
  excel: {
    keywords: [
      'excel',
      'spreadsheet',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml',
    ],
    // icon: '📊',
    render: (docValue, docName) => (
      <div className="document-preview">
        <div className="document-icon">📊</div>
        <a
          href={docValue}
          download={docName}
          className="document-download-link"
        >
          Download {docName}
        </a>
      </div>
    ),
  },
  powerpoint: {
    keywords: [
      'powerpoint',
      'presentation',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml',
    ],
    // icon: '📊',
    render: (docValue, docName) => (
      <div className="document-preview">
        <div className="document-icon">📊</div>
        <a
          href={docValue}
          download={docName}
          className="document-download-link"
        >
          Download {docName}
        </a>
      </div>
    ),
  },
  text: {
    keywords: ['text/', 'text/plain', 'application/json'],
    // icon: '📝',
    render: (docValue, docName) => (
      <iframe
        src={docValue}
        title={docName}
        className="document-iframe document-iframe-text"
      />
    ),
  },
};

/**
 * DocumentRenderer Component
 *
 * Renders different document types based on their MIME type
 * Supports: PDF, Images, Word, Excel, PowerPoint, Text, and more
 *
 * @param {Object} document - Document object containing type, name, and value
 */
const DocumentRenderer = ({ document }) => {
  const docType = document.document_type || document.documentType || '';
  const docName = document.document_name || document.documentName || 'Document';
  const docValue = document.document || document.document || '';

  // Find matching document config
  const getDocumentConfig = () => {
    const lowerDocType = docType.toLowerCase();

    // Check for TIFF first (before general image check)
    if (
      lowerDocType.includes('tiff') ||
      lowerDocType.includes('tif') ||
      docValue.startsWith('data:image/tiff')
    ) {
      return DOCUMENT_CONFIGS.tiff;
    }

    for (const [key, config] of Object.entries(DOCUMENT_CONFIGS)) {
      if (
        config.keywords.some((keyword) =>
          lowerDocType.includes(keyword.toLowerCase())
        )
      ) {
        return config;
      }
    }

    // Check if value starts with data:image/ (fallback for images)
    if (docValue.startsWith('data:image/')) {
      return DOCUMENT_CONFIGS.image;
    }

    // Check if value starts with data: (try iframe)
    if (docValue.startsWith('data:')) {
      return {
        // icon: '📎',
        render: (value, name) => (
          <div className="document-preview">
            <iframe src={value} title={name} className="document-iframe" />
            <a href={value} download={name} className="document-download-link">
              Download
            </a>
          </div>
        ),
      };
    }

    // Default fallback
    return {
      // icon: '📎',
      render: (value, name) => (
        <div className="document-preview">
          <div className="document-icon">📎</div>
          <a href={value} download={name} className="document-download-link">
            Download Document
          </a>
        </div>
      ),
    };
  };

  const config = getDocumentConfig();

  return (
    <div className="document-renderer">
      <div className="document-header pb-1">
        {/* <span className="document-icon-small">{config.icon}</span> */}
        <span className="document-name">{docName}</span>
      </div>

      <div className="document-content">
        {config.render(docValue, docName, docType)}
      </div>
    </div>
  );
};

export default DocumentRenderer;
